import { Suspense } from 'react';
import { Box, Text, Badge, Spinner } from '@chakra-ui/react';
import { ADMIN_COLORS, ADMIN_LAYOUT } from '../../../../../generated/tokens/constants';
import type {
  Block,
  PreviewMode,
  ImageBlock as ImageBlockType,
  ColumnsBlock as ColumnsBlockType,
  ContentSourceBlock as ContentSourceBlockType,
  EntityCardBlock as EntityCardBlockType,
  PanelGridBlock as PanelGridBlockType,
  ImageCarouselBlock as ImageCarouselBlockType,
} from '../editor/types';
// Eagerly loaded — basic 6
import { HeadingBlock } from './blocks/HeadingBlock';
import { TextBlock } from './blocks/TextBlock';
import { ImageBlock } from './blocks/ImageBlock';
import { ButtonBlock } from './blocks/ButtonBlock';
import { SpacerBlock } from './blocks/SpacerBlock';
import { DividerBlock } from './blocks/DividerBlock';
// Lazy loaded — advanced 11
import {
  LazyColumnsBlock,
  LazyVideoBlock,
  LazyContentSourceBlock,
  LazyEntityCardBlock,
  LazyEmbedFlowBlock,
  LazyIconGridBlock,
  LazyStatsRowBlock,
  LazyAccordionBlock,
  LazyTabsBlock,
  LazyTestimonialBlock,
  LazyCountdownTimerBlock,
  LazyPanelGridBlock,
  LazyImageCarouselBlock,
  LazyCalloutBlock,
} from './blocks/lazyBlocks';

interface BlockRendererProps {
  block: Block;
  previewMode?: PreviewMode;
  isEditorPreview?: boolean;
}

function LazyFallback() {
  return (
    <Box p={4} display="flex" justifyContent="center">
      <Spinner size="sm" color="blaze.500" />
    </Box>
  );
}

/**
 * Routes a Block to the appropriate block component.
 * Basic 6 are eagerly loaded; advanced 11 are lazy-loaded.
 */
export function BlockRenderer({ block, previewMode: _previewMode, isEditorPreview }: BlockRendererProps): React.ReactElement {
  switch (block.type) {
    // === Eager: Basic 6 ===
    case 'heading':
      return <HeadingBlock {...block.props} blockId={block.id} />;
    case 'text':
      return <TextBlock {...block.props} blockId={block.id} />;
    case 'image':
      return <ImageBlock {...(block as ImageBlockType)} blockId={block.id} />;
    case 'button':
      return <ButtonBlock {...block.props} blockId={block.id} />;
    case 'spacer':
      return <SpacerBlock {...block.props} />;
    case 'divider':
      return <DividerBlock {...block.props} />;

    // === Lazy: Advanced 11 ===
    case 'columns':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyColumnsBlock {...(block as ColumnsBlockType).props} _stagger={(block as ColumnsBlockType).animation?.stagger} _animation={(block as ColumnsBlockType).animation} />
        </Suspense>
      );
    case 'video':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyVideoBlock {...block.props} />
        </Suspense>
      );
    case 'content-source':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyContentSourceBlock {...(block as ContentSourceBlockType).props} resolved_data={(block as ContentSourceBlockType).resolved_data} isEditorPreview={isEditorPreview} />
        </Suspense>
      );
    case 'entity-card':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyEntityCardBlock {...(block as EntityCardBlockType).props} resolved_data={(block as EntityCardBlockType).resolved_data as Array<{ type: string; id: number; data: Record<string, unknown> }> | undefined} />
        </Suspense>
      );
    case 'embed-flow':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyEmbedFlowBlock {...block.props} isEditorPreview={isEditorPreview} />
        </Suspense>
      );
    case 'icon-grid':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyIconGridBlock {...block.props} resolved_data={(block as unknown as { resolved_data?: { icon_images?: Record<number, Record<string, string>> } }).resolved_data} blockId={block.id} _stagger={block.animation?.stagger} _animation={block.animation} />
        </Suspense>
      );
    case 'stats-row':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyStatsRowBlock {...block.props} />
        </Suspense>
      );
    case 'accordion':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyAccordionBlock {...block.props} blockId={block.id} />
        </Suspense>
      );
    case 'tabs':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyTabsBlock {...block.props} />
        </Suspense>
      );
    case 'testimonial':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyTestimonialBlock {...block.props} resolved_data={(block as unknown as { resolved_data?: { avatar_data?: Record<string, string> } }).resolved_data} blockId={block.id} />
        </Suspense>
      );
    case 'countdown-timer':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyCountdownTimerBlock {...block.props} />
        </Suspense>
      );
    case 'panel-grid':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyPanelGridBlock {...(block as PanelGridBlockType).props} resolved_data={(block as PanelGridBlockType).resolved_data} />
        </Suspense>
      );
    case 'image-carousel':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyImageCarouselBlock {...(block as ImageCarouselBlockType).props} resolved_data={(block as ImageCarouselBlockType).resolved_data} />
        </Suspense>
      );
    case 'callout':
      return (
        <Suspense fallback={<LazyFallback />}>
          <LazyCalloutBlock {...block.props} resolved_data={(block as unknown as { resolved_data?: { icon_data?: Record<string, string> } }).resolved_data} />
        </Suspense>
      );

    default:
      return (
        <Box
          gc-testing-id="blockRendererUnknown"
          p={4}
          borderRadius={ADMIN_LAYOUT.radius}
          bg={ADMIN_COLORS.card}
          border="1px dashed"
          borderColor={ADMIN_COLORS.border}
        >
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Badge gc-testing-id="badgeBlockType" colorScheme="purple" fontSize="xs">
              {(block as unknown as { type: string }).type}
            </Badge>
            <Text fontSize="xs" color={ADMIN_COLORS.textMuted} fontFamily="mono">
              {(block as unknown as { id: string }).id.slice(0, 8)}
            </Text>
          </Box>
          <Text fontSize="sm" color={ADMIN_COLORS.textSecondary}>
            Unknown block type
          </Text>
        </Box>
      );
  }
}
